import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Lock, Mail, User } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Store token and admin info
      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_info", JSON.stringify(response.data.admin));
      
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      navigate("/admin/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "An error occurred";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#F4F4F0] flex items-center justify-center px-6 py-24"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] mb-4">
            {isLogin ? "Admin Login" : "Create Account"}
          </h1>
          <p className="text-[#1A1A1A]/60">
            {isLogin
              ? "Sign in to manage your website content"
              : "Register to access the admin dashboard"}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="admin-login-form"
          className="space-y-6 bg-white p-8 border border-[#1A1A1A]/10"
        >
          {!isLogin && (
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-3">
                Full Name
              </label>
              <div className="relative">
                <User
                  size={18}
                  className="absolute left-0 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40"
                />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!isLogin}
                  data-testid="admin-name-input"
                  className="w-full bg-transparent border-b border-[#1A1A1A]/20 pl-8 pr-0 py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none transition-colors"
                  placeholder="John Doe"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-3">
              Email Address
            </label>
            <div className="relative">
              <Mail
                size={18}
                className="absolute left-0 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="admin-email-input"
                className="w-full bg-transparent border-b border-[#1A1A1A]/20 pl-8 pr-0 py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none transition-colors"
                placeholder="admin@iidas.in"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-3">
              Password
            </label>
            <div className="relative">
              <Lock
                size={18}
                className="absolute left-0 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40"
              />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="admin-password-input"
                className="w-full bg-transparent border-b border-[#1A1A1A]/20 pl-8 pr-10 py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none transition-colors"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40 hover:text-[#1A1A1A]"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            data-testid="admin-submit-btn"
            className="w-full bg-[#1A1A1A] text-[#F4F4F0] py-4 uppercase tracking-widest text-xs hover:bg-[#3A4D39] transition-colors duration-300 disabled:opacity-50"
          >
            {isLoading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
          </button>
        </form>

        <p className="text-center mt-6 text-sm text-[#1A1A1A]/60">
          {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            onClick={() => setIsLogin(!isLogin)}
            data-testid="toggle-auth-mode"
            className="text-[#3A4D39] hover:underline"
          >
            {isLogin ? "Register" : "Sign In"}
          </button>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
